import React, { useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { storage } from "../utils/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../context/AuthContext";
import AdminEventsPanel from "../components/AdminEventsPanel";
import AdminUsersPage from "./AdminUsersPage";
import { db } from "../utils/firebase";

function AddEventDialog({ open, onClose, onEventAdded }) {
  const [form, setForm] = useState({
    name: "",
    date: null,
    generalLocation: "",
    specificLocation: "",
    description: "",
    image: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }
  function handleDateChange(newValue) {
    setForm({ ...form, date: newValue });
  }
  function handleFile(e) {
    setForm({ ...form, image: e.target.files[0] });
  }

  const { user, role } = useAuth();

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      let imageUrl = "";
      if (form.image) {
        const storageRef = ref(
          storage,
          `eventImages/${Date.now()}_${form.image.name}`
        );
        await uploadBytes(storageRef, form.image);
        imageUrl = await getDownloadURL(storageRef);
      }
      // Actually add event to Firestore
      const { collection, addDoc, Timestamp } = await import("firebase/firestore");
      await addDoc(collection(db, "events"), {
        name: form.name,
        date: form.date ? (form.date instanceof Date ? form.date : new Date(form.date)) : null,
        generalLocation: form.generalLocation,
        specificLocation: form.specificLocation,
        description: form.description,
        imageUrl,
        archived: false,
        createdAt: new Date(),
        mode: 'signups', // default to open signups
        visible: true,   // default to visible for regular users
      });
      if (onEventAdded) onEventAdded();
      onClose();
    } catch (err) {
      setError("Failed to add event. Please try again.");
      setSubmitting(false);
    } finally {
      setSubmitting(false);
    }
  }
  // Return Dialog JSX
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add New Event
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Event Name"
              name="name"
              required
              value={form.name}
              onChange={handleChange}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Date & Time"
                value={form.date}
                onChange={handleDateChange}
                renderInput={(params) => <TextField {...params} required fullWidth />} 
              />
            </LocalizationProvider>
            <TextField
              label="General Location (public)"
              name="generalLocation"
              required
              value={form.generalLocation}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Specific Location (hidden until paid)"
              name="specificLocation"
              required
              value={form.specificLocation}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Description"
              name="description"
              multiline
              rows={3}
              value={form.description}
              onChange={handleChange}
              fullWidth
            />
            <Button variant="outlined" component="label">
              {form.image ? form.image.name : "Upload Event Image"}
              <input type="file" hidden accept="image/*" onChange={handleFile} />
            </Button>
            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Saving..." : "Add Event"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function AdminDashboard({ onLogout }) {
  const [addOpen, setAddOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { collection, getDocs } = await import("firebase/firestore");
      const [eventsSnap, participantsSnap] = await Promise.all([
        getDocs(collection(db, "events")),
        getDocs(collection(db, "participants"))
      ]);
      const allParticipants = participantsSnap.docs.map(p => ({ id: p.id, ...p.data() }));
      // Build participant lookup by id for quick resolution
      const participantById = new Map(allParticipants.map(p => [p.id, p]));
      const eventsArr = eventsSnap.docs.map(docSnap => {
        const event = { id: docSnap.id, ...docSnap.data() };
        // Prefer explicit participants array; fallback by eventId
        let eventParticipants = [];
        if (Array.isArray(event.participants) && event.participants.length) {
          eventParticipants = event.participants
            .map(pid => participantById.get(pid))
            .filter(Boolean);
        } else {
          eventParticipants = allParticipants.filter(p => p.eventId === event.id);
        }
        let male = 0, female = 0;
        const participantNames = [];
        eventParticipants.forEach(p => {
          const gender = (p.gender || '').toLowerCase();
          if (gender === 'male') male++; else if (gender === 'female') female++;
          if (p.name) participantNames.push(p.name);
          else if (p.email) participantNames.push(p.email.split('@')[0]);
        });
        return {
          ...event,
          maleCount: male,
            femaleCount: female,
          totalCount: eventParticipants.length,
          participantNames
        };
      });
      setEvents(eventsArr);
    } catch (err) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchEvents();
  }, []);

  return (
    <Routes>
      <Route
        path="/users"
        element={<AdminUsersPage onBack={() => navigate("/admin")} />}
      />
      <Route
        path="*"
        element={
          <Box
            sx={{
              minHeight: "calc(100vh - 64px)",
              background: "#fafafa",
              m: 0,
              p: 0,
              py: { xs: 2, sm: 4 },
            }}
          >
            <Box sx={{ px: { xs: 1, sm: 2, md: 8 } }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                mb={3}
              >
                <Typography variant="h4" flexGrow={1} sx={{ fontSize: { xs: 22, sm: 28 } }}>
                  Admin Dashboard
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/admin/users")}
                  sx={{
                    mr: { xs: 0, sm: 2 },
                    mb: { xs: 1, sm: 0 },
                    maxWidth: { xs: '100%', sm: 170 },
                    minWidth: { xs: '100%', sm: 110 },
                    px: { xs: 2, sm: 2 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  fullWidth={true}
                >
                  User Management
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddOpen(true)}
                  sx={{
                    maxWidth: { xs: '100%', sm: 170 },
                    minWidth: { xs: '100%', sm: 110 },
                    px: { xs: 2, sm: 2 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  fullWidth={true}
                >
                  Add Event
                </Button>
              </Stack>
              <AddEventDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onEventAdded={fetchEvents}
              />
              <Box mt={4}>
                <AdminEventsPanel events={events} loading={loading} />
              </Box>
            </Box>
          </Box>
        }
      />
    </Routes>
  );
}

export default AdminDashboard;